import { getSsoSession } from 'auth/ssoClient'
import { cfg } from 'config/runtimeConfig'
import type { ActionWidgetType } from 'types/entity'
import type { Brief } from './buildBrief'
import type { PendingOp, RecordMemory } from './memoryApi'
import { openItemsForAi } from './memoryView'
import { formatWhen } from './parseWhen'

/**
 * The paid AI read of a record.
 *
 * The model is never called from the browser: a key in the page is a key anyone can copy, and a
 * call that skips the server skips the wallet. This posts the record's story to the AI service
 * (REACT_APP_AI_API_URL - the analytics API's origin), which checks the balance, calls the model,
 * deducts the credits and answers with what it charged. With no service configured there is
 * nothing to call, and the panel says so instead of showing a button that does nothing.
 *
 * What it costs is never written here. The tooltip reads the account's live rate and balance from
 * the same endpoint the analytics Ask box uses, so a price change shows up without a release.
 *
 * Contract - POST {origin}/api/ai/record-insights, Authorization: Bearer <SSO token>
 *   request : { entityType, entityId, facts: {...}, timeline: [{ at, channel, direction, who, title, text }] }
 *   200     : { steps: [{ text, why, action? }], draft?: { channel, text }, creditsUsed, balance? }
 *   402     : not enough credits (nothing charged)
 */

const AI_API_ORIGIN: string = (cfg('REACT_APP_AI_API_URL') ?? '').trim().replace(/\/+$/, '')

/** Empty when no AI service is configured - the panel then offers no AI row. */
export const AI_INSIGHTS_URL: string = AI_API_ORIGIN ? `${AI_API_ORIGIN}/api/ai/record-insights` : ''
const AI_CREDIT_URL: string = AI_API_ORIGIN ? `${AI_API_ORIGIN}/api/rb/ai-credit` : ''

/** GET /api/rb/ai-credit - the account's AI wallet: live rate, worst-case cost of one read, balance. */
export interface AiCreditInfo {
  billingEnabled: boolean
  balanceAvailable: boolean
  balance: number
  unitsPerCredit: number
  pricePerCredit: number
  estCreditsPerAsk: number
  estInrPerAsk: number
}

export async function fetchAiCreditInfo(signal?: AbortSignal): Promise<AiCreditInfo | null> {
  if (!AI_CREDIT_URL) return null
  try {
    const res = await fetch(AI_CREDIT_URL, { headers: { Authorization: `Bearer ${getSsoSession().token ?? ''}` }, signal })
    return res.ok ? ((await res.json()) as AiCreditInfo) : null
  } catch {
    return null // the tooltip then explains the charging without figures, rather than guessing them
  }
}

/** The newest entries are what matter; this also bounds what a single read can cost. */
const MAX_TIMELINE_ENTRIES = 60
const MAX_ENTRY_CHARS = 600

export interface AiStep {
  text: string
  why: string
  action?: ActionWidgetType
}

/** What the same read did to the record's Customer Memory; absent when memory was not asked for. */
export interface AiMemoryOutcome {
  saved: boolean
  added: number
  closed: number
  reworded: number
  /** Operations the model proposed that were not believed (no matching words in the history...). */
  rejected: number
  summary: string | null
  /** Only when not saved: the verified operations, so the paid work can still be stored. */
  pendingOps: PendingOp[]
  pendingReadTo: string | null
  error: string | null
}

export interface AiInsights {
  steps: AiStep[]
  draft?: { channel: string; text: string }
  creditsUsed: number
  balance?: number
  memory?: AiMemoryOutcome
}

export class AiInsightsError extends Error {
  constructor(message: string, readonly kind: 'no-credits' | 'failed' | 'signed-out') {
    super(message)
  }
}

const SIGNED_OUT = 'The AI service did not accept your sign-in - it has most likely timed out. Sign in again and you will come straight back to this record. Nothing was charged.'

/**
 * Whether a sign-in is still good is the server's call, never this page's.
 *
 * An earlier version read the token's `exp` here and refused to call once it had passed. That
 * turned a guess into a gate nothing could open: the sign-in page does not mint a token per visit,
 * it hands back the one it has cached for as long as ITS check passes - so "sign in again"
 * returned the same token, this page judged it expired again, and the person was stuck. A wasted
 * request costs a fraction of a second; a wrong verdict here cost the feature. So the request is
 * always made, and only a 401 means signed out.
 *
 * The same caching is why re-signing-in can fail to help when the server really has rejected a
 * token. The rejected token is remembered for the tab; if the next sign-in brings the very same
 * one back, the only way to a new token is a full sign-out first.
 */
const REJECTED_TOKEN_KEY = 'insights:rejectedToken'

export function rememberRejectedToken(): void {
  try {
    sessionStorage.setItem(REJECTED_TOKEN_KEY, getSsoSession().token ?? '')
  } catch {
    /* no session storage: the plain re-sign-in is still offered */
  }
}

/** True when the token now held is one the AI service has already turned away in this tab. */
export function signInWasAlreadyRejected(): boolean {
  try {
    const rejected = sessionStorage.getItem(REJECTED_TOKEN_KEY)
    return !!rejected && rejected === (getSsoSession().token ?? '')
  } catch {
    return false
  }
}

export async function askAiInsights(
  entityType: 'lead' | 'enquiry',
  entityId: string,
  brief: Brief,
  memory: RecordMemory | null,
  signal?: AbortSignal,
): Promise<AiInsights> {
  const session = getSsoSession()
  // The newest dated entry becomes the memory's new watermark. It is sent as the account's wall
  // clock with no zone - the same clock every history API here speaks.
  const newest = brief.timeline.find((t) => t.when)?.when ?? null
  const body = {
    entityType,
    entityId,
    // Asking for the memory to be kept costs a little more output, never a second call. It is only
    // asked when the record's memory was actually read - an update built on a list we could not
    // see would re-add everything already there.
    memory: memory ? { enabled: true, items: openItemsForAi(memory.items), readUpTo: memory.readUpTo, newestAt: newest ? wallClockIso(newest) : null } : undefined,
    facts: {
      headline: brief.headline.text,
      waitingOnUs: brief.waitingOnUs?.body ?? null,
      lastRemark: brief.lastWords?.body ?? null,
      planned: brief.nextSteps.map((s) => ({ kind: s.kind, label: s.label, due: formatWhen(s.when, s.hasTime), overdueDays: s.overdueDays })),
    },
    timeline: brief.timeline.slice(0, MAX_TIMELINE_ENTRIES).map((t) => ({
      at: t.when ? formatWhen(t.when, t.hasTime) : t.whenText,
      channel: t.channel,
      direction: t.direction ?? null,
      who: t.who,
      title: t.title,
      text: t.body.slice(0, MAX_ENTRY_CHARS),
    })),
  }

  let response: Response
  try {
    response = await fetch(AI_INSIGHTS_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.token ?? ''}` },
      body: JSON.stringify(body),
      signal,
    })
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err
    throw new AiInsightsError('The AI service could not be reached. Nothing was charged.', 'failed')
  }

  if (response.status === 401) {
    rememberRejectedToken()
    throw new AiInsightsError(SIGNED_OUT, 'signed-out')
  }
  if (response.status === 402) {
    throw new AiInsightsError('There are not enough AI credits in the wallet for this. Nothing was charged.', 'no-credits')
  }
  if (!response.ok) {
    throw new AiInsightsError(`The AI service answered with an error (${response.status}). Nothing was charged.`, 'failed')
  }

  const data = (await response.json()) as Partial<AiInsights> & { memory?: Partial<AiMemoryOutcome> | null }
  const m = data.memory
  return {
    memory: m
      ? {
          saved: m.saved === true,
          added: Number(m.added) || 0,
          closed: Number(m.closed) || 0,
          reworded: Number(m.reworded) || 0,
          rejected: Number(m.rejected) || 0,
          summary: typeof m.summary === 'string' ? m.summary : null,
          pendingOps: Array.isArray(m.pendingOps) ? m.pendingOps : [],
          pendingReadTo: typeof m.pendingReadTo === 'string' ? m.pendingReadTo : null,
          error: typeof m.error === 'string' ? m.error : null,
        }
      : undefined,
    steps: Array.isArray(data.steps) ? data.steps.filter((s) => s && typeof s.text === 'string') : [],
    draft: data.draft && typeof data.draft.text === 'string' ? data.draft : undefined,
    creditsUsed: Number(data.creditsUsed) || 0,
    balance: typeof data.balance === 'number' ? data.balance : undefined,
  }
}

const two = (n: number) => String(n).padStart(2, '0')

/** "2026-09-19T17:16:00" in the browser's own clock - no zone, no conversion. */
function wallClockIso(d: Date): string {
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}T${two(d.getHours())}:${two(d.getMinutes())}:${two(d.getSeconds())}`
}
